import React, { useEffect } from "react";
import { Badge } from "../../../../ui/components/Badge";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import { FeatherCheck, FeatherAlertTriangle } from "@subframe/core";
import { ValidationResponse } from "../../domain/entities/ValidationEntity";
import { InvoiceEntity } from "../../domain/entities/InvoiceEntity";
import { useInvoiceValidation } from "../hooks/useInvoiceValidation";
import { AI_DETECTION_FINAL_ERROR_MESSAGE } from "../../../../shared/constants/aiDetectionMessages";

interface AIDetectionOverviewInvoicesProps {
  invoiceData: any; // InvoiceData type
  invoiceDocument: InvoiceEntity;
  onValidationComplete?: (result: ValidationResponse) => void;
  onLoadingChange?: (loading: boolean) => void;
  // True once the user edits the invoice-number field on this screen. The
  // Document Number Check validates the SAVED value (DB), so after an edit its
  // failure is stale — we hide it and show an informational note instead.
  invoiceNumberEdited?: boolean;
}

const AIDetectionOverviewInvoices: React.FC<
  AIDetectionOverviewInvoicesProps
> = ({
  invoiceData,
  invoiceDocument,
  onValidationComplete,
  onLoadingChange,
  invoiceNumberEdited = false,
}) => {
  // Use the custom hook for validation
  const { validationData, loading, error } = useInvoiceValidation({
    invoiceData,
    invoiceDocument,
    onValidationComplete,
  });

  useEffect(() => {
    onLoadingChange?.(loading);
    return () => {
      onLoadingChange?.(false);
    };
  }, [loading, onLoadingChange]);

  if (loading) {
    return (
      <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-warning-200 px-6 py-6 bg-gradient-to-r from-warning-50 to-warning-100">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-warning-500">
            <span className="text-body-bold font-body-bold text-white">AI</span>
          </div>
          <span className="text-heading-3 font-heading-3 text-default-font">
            Running AI Detection...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-error-200 px-6 py-6 bg-gradient-to-r from-error-50 to-error-100">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-error-500">
            <span className="text-body-bold font-body-bold text-white">AI</span>
          </div>
          <span className="text-heading-3 font-heading-3 text-default-font">
            {AI_DETECTION_FINAL_ERROR_MESSAGE}
          </span>
        </div>
      </div>
    );
  }

  if (!validationData) {
    return null;
  }

  const { validation_summary } = validationData;
  const passedCount = validation_summary.passed_validations;
  const totalCount = validation_summary.total_validations;
  const failedCount = validation_summary.failed_validations;

  // Display-label overrides for checks whose auto-capitalized key reads wrong
  // (e.g. export_lut_check → "Export Lut Check"). Falls back to the generic
  // underscore→Title Case transform for everything else.
  const VALIDATION_LABELS: Record<string, string> = {
    export_lut_check: "LUT Check",
    hsn_check: "HSN Check",
    hsn_description_check: "HSN Description Check",
    hsn_rate_check: "HSN Rate Check",
    // ZOUR-65: which GST registration the document belongs to. Named for what
    // the reviewer must DO, not for the internal key.
    location_missing: "Location Not Identified",
    location_unconfirmed: "Location — Please Confirm",
    location_inactive: "Location No Longer Active",
    seller_gstin_not_ours: "Seller GSTIN Not Yours",
    // ZOUR-102: the customer GSTIN is one of ours — a sale to ourselves.
    customer_gstin_is_ours: "Customer GSTIN Is Your Own",
    // ZOUR-19: raised when a document predates the GST registration date
    // and carried tax that was dropped.
    gst_not_applicable_before_date: "GST Not Applicable (Before Registration)",
  };
  const formatValidationName = (name: string): string =>
    VALIDATION_LABELS[name] ??
    name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  // Filter validation details into passed and failed arrays
  const passedValidations = validation_summary.validation_details.filter(
    (validation) => validation.valid,
  );
  const allFailedValidations = validation_summary.validation_details.filter(
    (validation) => !validation.valid,
  );

  // The Document Number Check validates the number saved in the DB, not the
  // value being edited on screen. Once the user edits the field, its failure is
  // stale — hide it from Issues Found and show an informational note instead.
  // The saved value is re-verified on Save & Go Back / post.
  const hiddenDocNumberCheck = invoiceNumberEdited
    ? allFailedValidations.find(
        (validation) => validation.validation_name === "document_number_check",
      )
    : undefined;
  const failedValidations = hiddenDocNumberCheck
    ? allFailedValidations.filter(
        (validation) => validation.validation_name !== "document_number_check",
      )
    : allFailedValidations;
  const displayedFailedCount = hiddenDocNumberCheck
    ? failedCount - 1
    : failedCount;
  const displayedTotalCount = hiddenDocNumberCheck ? totalCount - 1 : totalCount;

  return (
    <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-warning-200 px-6 py-6 bg-gradient-to-r from-warning-50 to-warning-100">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-warning-500">
          <span className="text-body-bold font-body-bold text-white">AI</span>
        </div>
        <div className="flex items-start gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">
            AI Detection Overview
          </span>
          <Badge variant="warning" icon={<FeatherCheck />}>
            {passedCount}/{displayedTotalCount} Checks Passed
          </Badge>
        </div>
      </div>
      <div className="flex w-full items-start gap-6">
        <div
          className={`flex ${displayedFailedCount === 0 ? "w-full" : "grow shrink-0 basis-0"} flex-col items-start gap-4 rounded-md border border-solid border-success-200 bg-success-50 px-4 py-4`}
        >
          <div className="flex items-center gap-2">
            <IconWithBackground variant="success" size="small" />
            <span className="text-heading-3 font-heading-3 text-success-700">
              Passed Checks ({passedCount})
            </span>
          </div>
          <div className="flex w-full flex-col items-start gap-2">
            {passedValidations.map((validation, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex h-1.5 w-1.5 flex-none items-start rounded-full bg-success-500" />
                <span className="text-body font-body text-success-700">
                  {formatValidationName(validation.validation_name)}:{" "}
                  {validation.message}
                </span>
              </div>
            ))}
          </div>
        </div>
        {displayedFailedCount > 0 && (
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-4">
            <div className="flex items-center gap-2">
              <IconWithBackground
                variant="error"
                size="small"
                icon={<FeatherAlertTriangle />}
              />
              <span className="text-heading-3 font-heading-3 text-error-700">
                Issues Found ({displayedFailedCount})
              </span>
            </div>
            <div className="flex w-full flex-col items-start gap-2">
              {failedValidations.map((validation, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="flex h-1.5 w-1.5 flex-none items-start rounded-full bg-error-500" />
                  <span className="text-body font-body text-error-700">
                    {formatValidationName(validation.validation_name)}:{" "}
                    {validation.message}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIDetectionOverviewInvoices;
